import { UiRenderer } from "@agent/UiRenderer";
import { isUiPayload } from "@agent/uiSchema";
import {
  type CardSeverity,
  SeverityBadge,
  SeverityReason,
  severityVariant,
} from "@subjects/cardSeverity";
import { SignTaskCard } from "@tasks/SignTaskCard";
import { TaskCard, TaskResolutionActions } from "@tasks/TaskCard";
import { HUMAN, SIGN, type Task } from "@tasks/tasksClient";

/**
 * One outstanding task, rendered the same way in the urgent zone, pager, and
 * see-all list: a2ui payloads render their own action buttons via
 * `UiRenderer`, and plain `human` to-dos get Mark done / Dismiss, both
 * resolved clinic-wide through `PATCH /tasks` (WEB-3199). Severity chrome
 * (WEB-3195) frames either shape the same way: a status-ink ring on the card
 * and the word under a highlighter.
 *
 * The default paper presentation. The /tasks route renders the same three shapes
 * as index cards instead (`TaskIndexCard`); both read the same task and share the
 * same hooks, so a contract change lands in one place and only the paper differs.
 */
export const TaskCardItem = ({
  task,
  severity,
  handleAction,
}: {
  task: Task;
  severity: CardSeverity;
  handleAction: (
    task: Task,
    action: string,
    data: Record<string, unknown>,
  ) => void;
}) =>
  task.kind === SIGN ? (
    <SignTaskCard
      task={task}
      view={task.views[0]}
      onDecline={() => handleAction(task, "dismiss", {})}
    />
  ) : isUiPayload(task.payload) ? (
    // An a2ui tree brings its own card, so the ring goes on that card rather
    // than on a wrapper around it (`cardVariant`), and the severity's word,
    // which a ring on its own can't say, is written above it: the one line of
    // this card's chrome that isn't the agent's own copy.
    <div className="flex flex-col gap-1.5">
      {severity !== "calm" && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <SeverityBadge
            severity={severity}
            dataTestId={`task-severity-${task.id}`}
          />
          <SeverityReason reason={task.reason} />
        </div>
      )}
      <UiRenderer
        payload={task.payload}
        storeKey={task.id}
        onAction={(action, data) => handleAction(task, action, data)}
        allowDismiss={false}
        cardVariant={severityVariant(severity)}
      />
    </div>
  ) : (
    <TaskCard
      task={task}
      severity={severity}
      actions={
        task.kind === HUMAN && (
          <TaskResolutionActions
            task={task}
            onComplete={() => handleAction(task, "complete", {})}
            onDismiss={() => handleAction(task, "dismiss", {})}
          />
        )
      }
    />
  );
